"use client";

import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@scott/contentful/live-preview-init";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, useTransition } from "react";
import {
  GeometryTable,
  type GeometryTableProps,
} from "@/scott/ui/blocks/geometry-table/geometry-table";
import { columnWidthMap } from "@/services/contentful/components/GeometryTable/geometry-table.adapter";
import type { UiConfigurationInput } from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";

export type GeometryDataLiveEntry = {
  __typename: "GeometryTable";
  sys?: { id: string };
  internalName: string;
  anchorId?: string;
  productCodes?: string[];
  showUnitToggle?: boolean | null;
  defaultUnit?: GeometryTableProps["defaultUnit"];
  containerWidth?: keyof typeof columnWidthMap;
  // Microcopy fields — stored flat in Contentful, nested in GeometryTableProps.microcopies
  title?: string | null;
  frameSize?: string | null;
  sizesIn?: string | null;
  unitMm?: string | null;
  unitInches?: string | null;
  // UiConfiguration fields — compared field-by-field to avoid reference mismatches
  backgroundColor?: UiConfigurationInput["backgroundColor"];
  paddingTop?: UiConfigurationInput["paddingTop"];
  paddingBottom?: UiConfigurationInput["paddingBottom"];
};

type Props = {
  sysId?: string;
  tableProps: GeometryTableProps;
  liveEntry: GeometryDataLiveEntry;
};

const EDITOR_REFRESH_DEBOUNCE_MS = 1000;
// Extra delay before retrying when the first refresh returned stale data.
// Gives Contentful's auto-save enough time to commit the draft.
const STALE_RETRY_DELAY_MS = 1500;

const shimmerKeyframes = `
@keyframes geo-preview-shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
`;

type DisplayFieldDescriptor = {
  field: keyof GeometryDataLiveEntry;
  // Override the tableProps key when it differs from the field name (e.g. containerWidth → columnWidth).
  tableKey?: keyof GeometryTableProps;
  // true: value goes into tableProps.microcopies instead of tableProps directly.
  toMicrocopies?: boolean;
};

// Display-only fields: applied immediately as overrides, no server refresh needed.
// Commerce fields: require router.refresh() + product-API round-trip (shimmer shown).
// To add a new field: put it in the right array — that's the only place to touch.
const DISPLAY_FIELDS: readonly DisplayFieldDescriptor[] = [
  { field: "anchorId" },
  { field: "showUnitToggle" },
  { field: "defaultUnit" },
  // containerWidth stores a label ("narrow"/"medium"/"wide"); tableProps.columnWidth expects 8/10/12.
  // The value is converted via columnWidthMap inside buildLiveOverrides.
  { field: "containerWidth", tableKey: "columnWidth" },
  { field: "backgroundColor" },
  { field: "paddingTop" },
  { field: "paddingBottom" },
  { field: "title", toMicrocopies: true },
  { field: "frameSize", toMicrocopies: true },
  { field: "sizesIn", toMicrocopies: true },
  { field: "unitMm", toMicrocopies: true },
  { field: "unitInches", toMicrocopies: true },
];

const COMMERCE_FIELDS: ReadonlyArray<keyof GeometryDataLiveEntry> = [
  "productCodes",
];

function buildLiveOverrides(
  updated: GeometryDataLiveEntry,
  server: GeometryDataLiveEntry
) {
  const tableOverrides: Partial<GeometryTableProps> = {};
  const microcopies: Partial<GeometryTableProps["microcopies"]> = {};

  for (const { field, tableKey, toMicrocopies } of DISPLAY_FIELDS) {
    if (updated[field] === server[field]) {
      continue;
    }
    let value: unknown = updated[field];
    if (field === "containerWidth") {
      value = value
        ? columnWidthMap[value as keyof typeof columnWidthMap]
        : undefined;
    }
    if (toMicrocopies) {
      (microcopies as Record<string, unknown>)[tableKey ?? field] =
        value ?? undefined;
    } else {
      (tableOverrides as Record<string, unknown>)[tableKey ?? field] = value;
    }
  }

  const hasMicrocopyChange = Object.keys(microcopies).length > 0;
  const hasAnyChange =
    hasMicrocopyChange || Object.keys(tableOverrides).length > 0;

  if (!hasAnyChange) {
    return;
  }

  return { tableOverrides, microcopies, hasMicrocopyChange };
}

function hasCommerceChange(
  updated: GeometryDataLiveEntry,
  server: GeometryDataLiveEntry
): boolean {
  // productCodes is an array — use JSON.stringify for value equality instead of reference.
  return COMMERCE_FIELDS.some((field) => {
    const a = updated[field];
    const b = server[field];
    if (Array.isArray(a) || Array.isArray(b)) {
      return JSON.stringify(a ?? []) !== JSON.stringify(b ?? []);
    }
    return a !== b;
  });
}

export function GeometryDataBlockClientPreview({
  sysId,
  tableProps,
  liveEntry,
}: Readonly<Props>) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isShimmering, setIsShimmering] = useState(false);

  const isPendingRef = useRef(false);
  isPendingRef.current = isPending;

  const updatedEntry = useContentfulLiveUpdates(liveEntry);
  const prevEntryRef = useRef(liveEntry);

  // Always holds the latest server-delivered liveEntry so the effect can
  // compare against current server state without a stale closure.
  // Updated synchronously during render (before effects run).
  const liveEntryRef = useRef(liveEntry);
  liveEntryRef.current = liveEntry;

  const inspector = useContentfulInspectorMode({ entryId: sysId });

  // router.refresh and startTransition are stable references — safe to omit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only re-run on editor updates
  useEffect(() => {
    if (updatedEntry === prevEntryRef.current) {
      return;
    }

    // Refresh in flight: sync the ref only — don't start another shimmer cycle.
    if (isPendingRef.current) {
      prevEntryRef.current = updatedEntry;
      return;
    }

    prevEntryRef.current = updatedEntry;

    // Display-only fields (anchorId, showUnitToggle, etc.) are applied immediately
    // via tablePropsOverrides — no shimmer needed for them.
    if (!hasCommerceChange(updatedEntry, liveEntryRef.current)) {
      return;
    }

    setIsShimmering(true);
    const timer = setTimeout(() => {
      if (!isPendingRef.current) {
        startTransition(() => router.refresh());
      }
    }, EDITOR_REFRESH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [updatedEntry]);

  // Stable key derived from server-fetched product data; changes when the server
  // returns new geometry data after a refresh.
  // For productCodes changes, Contentful's auto-save may take longer than the debounce
  // window — in that case the first refresh returns stale data. The shimmer stays
  // visible and the retry timer fires until the key changes.
  const remountKey = JSON.stringify({
    measurements: tableProps.measurements,
    sizes: tableProps.sizes,
    imageUrl: tableProps.imageUrl,
  });

  // Remembers the remountKey from the last successful shimmer cycle so we can
  // detect whether the server actually returned new data after a refresh.
  const lastRemountKeyRef = useRef(remountKey);

  // While shimmering and idle: check if new server data arrived (remountKey
  // changed) and clear the shimmer, or schedule another refresh if data is
  // still stale (Contentful auto-save not committed yet).
  // biome-ignore lint/correctness/useExhaustiveDependencies: startTransition and router are stable — safe to omit
  useEffect(() => {
    if (!isShimmering || isPending) {
      return;
    }

    if (remountKey !== lastRemountKeyRef.current) {
      lastRemountKeyRef.current = remountKey;
      setIsShimmering(false);
      return;
    }

    const timer = setTimeout(() => {
      startTransition(() => router.refresh());
    }, STALE_RETRY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isShimmering, isPending, remountKey]);

  const liveOverrides = buildLiveOverrides(updatedEntry, liveEntry);

  const effectiveTableProps = liveOverrides
    ? {
        ...tableProps,
        ...liveOverrides.tableOverrides,
        microcopies: liveOverrides.hasMicrocopyChange
          ? {
              ...tableProps.microcopies,
              ...liveOverrides.microcopies,
            }
          : tableProps.microcopies,
      }
    : tableProps;

  return (
    <div className="relative">
      {isShimmering && (
        <>
          <style>{shimmerKeyframes}</style>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-50 overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-linear-to-r from-transparent via-gray-500/65 to-transparent"
              style={{ animation: "geo-preview-shimmer 1.5s infinite" }}
            />
          </div>
        </>
      )}
      <GeometryTable
        {...effectiveTableProps}
        inspectorProps={{
          container: inspector({ fieldId: "internalName" }) ?? undefined,
        }}
        key={remountKey}
      />
    </div>
  );
}
